/**
 * The clearance cross-section (Profile tab): what the cloud actually contains
 * at one station, drawn behind the envelope it is being judged against.
 *
 * This replaces a static drawing of the profile polygon with the station
 * dock's Section idea, adapted to the one structural difference between the
 * two analyses. Station volumes compares two SURFACES, so its section draws
 * two lines with the cut and fill hatched between them. A clearance run has no
 * surface pair — it has a cloud and a gauge — so the section draws the cloud's
 * own density and lights the cells that are inside the gauge. A per-offset
 * min/max band would have been the closer mirror and the wrong drawing: a wire
 * above an empty gap and a solid wall reduce to the same two numbers.
 *
 * Layout (audit §A5 / spec §B4, owner round 5): one 28 px top row — the
 * interval stepper and the shared in-chart legend (`DockChartLegend`), the key
 * hint behind an ⓘ — then the 1:1 drawing, pinned left, and a SIDE AREA that
 * takes the rest of the width. A 1:1 gauge is height-bound, so on a wide dock
 * most of the row is the side area's; it holds, as width allows:
 *  - the interval's figures (the definition list that used to be all of it);
 *  - "Where it fouls": the station's violations, deepest intrusion and
 *    tightest margin per envelope SIDE (walls, roof, floor), the worst side
 *    tinted — the first question after "how much" is "from where";
 *  - the corridor strip: violations per interval for the whole run, the
 *    walked interval marked, click/drag/arrows to move along it — the run's
 *    context the Chart tab otherwise needs a tab switch for.
 * Marginal histograms ride on the drawing's own axes (offset above, height
 * beside), so a column of intruding returns reads as a spike in line with it.
 * What gives way on a small dock, in order: the histograms (a short plot),
 * then the strip, then — under 700 px of pane — everything but the figures,
 * which fold into one row above the drawing. Nothing scrolls at h280.
 *
 * The section once had a "Violations only" toggle that changed what ◀ ▶
 * stepped to. It was invisible in effect, duplicated the dock's own violation
 * stepper one row up and shared its name with the point-cloud colour mode, so
 * it went (owner feedback, round 5); the corridor strip is the way to SEE
 * where the violating intervals are and go there.
 *
 * The CHROME is the docks' shared kit (`DockStepper`, `DockChartLegend`), the
 * station section's tooltip card and visually-hidden table, and visx axes in
 * the same tick font. Offsets are SIGNED metres (−2 … 0 … +2, "CL" under 0),
 * the convention every other clearance readout uses.
 *
 * Three layers, deliberately:
 *  - an SVG UNDER everything for the hairline grid, so a gridline never
 *    paints over a return.
 *  - a CANVAS for the grid. At 0.05 m cells a station is ~26 000 cells, and
 *    26 000 SVG rects is a layout the browser re-lays-out on every hover.
 *  - an SVG overlay for the envelope, axes, markers and the hover box — the
 *    parts that must stay crisp at any zoom and want real text.
 *
 * The arithmetic is in `@/lib/clearance/sectionModel`; this file is the
 * drawing, the interaction and the copy.
 */
import { DockChartLegend, type DockLegendItem } from '@/components/dock/DockChartLegend';
import { DockStepper } from '@/components/dock/DockToolbar';
import { DOCK_CONTROL, DOCK_TEXT } from '@/components/dock/dockStyles';
import {
  FLOATING_TOOLTIP_STYLE,
  HiddenDataTable,
  TICK_FONT,
  TooltipCard,
  tickLabelProps,
} from '@/components/StationVolumeChartParts';
import { LoadingBadge, LoadingBadgeKeyframes } from '@/components/ui/LoadingBadge';
import { VerdictBadge } from '@/components/VerdictChip';
import { useClearanceSection } from '@/hooks/useClearance';
import {
  CLEARANCE_FILL,
  CLEARANCE_K2_KEY,
  CLEARANCE_LEGEND_ORDER,
  NEAR_MISS_RUNGS,
  nearMissBandsOf,
} from '@/lib/clearance/chart';
import {
  colAt,
  density,
  heightAt,
  nearMissBandFor,
  offsetAt,
  peakCount,
  profileDistanceField,
  profilePolygonPoints,
  rowAt,
  sectionCellsAt,
  sectionCellState,
  sectionColumnRows,
  sectionTicks,
  sectionViewport,
  signedOffsetTick,
  toPxX,
  toPxY,
  worstLabelPlacement,
  type SectionCellState,
} from '@/lib/clearance/sectionModel';
import {
  binAtFraction,
  corridorBuckets,
  corridorCounts,
  corridorHeight,
  foulsBySide,
  profileOutwardSide,
  profileSideEdges,
  profileSideField,
  rankIntervals,
  sectionMarginals,
  worstSide,
  type CorridorTone,
  type EnvelopeSide,
} from '@/lib/clearance/sectionStats';
import type { ClearanceJson, ClearanceLayerMeta } from '@/lib/clearance/types';
import { clearanceVerdict, marginalBandM } from '@/lib/clearance/verdict';
import { formatFixed, formatSigned, formatStationRange, staOnly } from '@/lib/stationVolumes/format';
import type { SvPalette } from '@/lib/stationVolumes/tokens';
import { useSvPalette } from '@/lib/stationVolumes/useSvPalette';
import { cn } from '@/lib/utils';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { scaleLinear } from '@visx/scale';
import { useTooltip, useTooltipInPortal } from '@visx/tooltip';
import { Info, Video } from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';

/** The station section's margins, less its taller exaggeration label. */
const MARGIN = { top: 14, right: 16, bottom: 34, left: 52 } as const;
/** Extra top margin for the y-axis title when no histogram stands above the grid. */
const AXIS_TITLE_ROOM_PX = 8;
/**
 * Height of the row above the plot (stepper + legend, and the gap under it) —
 * only used until the ResizeObserver reports the plot's own box, so the first
 * frame is drawn at roughly the right size.
 */
const CHROME_H = 36;
/** At most this many rows in the visually-hidden table (SV's own cap). */
const SR_MAX_ROWS = 41;
/** Rough advance of one character of the 10 px semibold tick font, for
 *  placing the worst-point label before it can be measured. */
const LABEL_CHAR_PX = 6;
/** Spare width beside the gauge for the worst-point label and the offset
 *  axis title, which both sit past the grid's right edge. */
const LABEL_ROOM_PX = 110;
/** The side area's least width (the old `w-64` panel) and the gap before it. */
const PANEL_W = 256;
const PANEL_GAP = 12;
/** Below this pane width the panel folds above the plot (`@max-[700px]`). */
const NARROW_PX = 700;
/** Below this plot height the stats panel folds its near-miss ladder. */
const SHORT_PANEL_PX = 210;
/**
 * The marginal histograms: bar depth, and the gap to the grid. Drawn only
 * from this plot height up — under it the 32 px they take is a ninth of the
 * gauge, and the gauge is the drawing (they are the first thing to go).
 */
const HIST_PX = 28;
const HIST_GAP = 5;
const HIST_MIN_PLOT_H = 250;
/**
 * Side-area widths at which its blocks go side by side: figures | where it
 * fouls (with the strip as a full-width row under both), then all three in
 * columns — the strip a tall mini-chart of its own.
 */
const SIDE_TWO_PX = 420;
const SIDE_THREE_PX = 880;
/** At most this many bars in the corridor strip (pooled beyond). */
const STRIP_MAX_BARS = 480;
/** Plot heights the stacked side area needs for its second and third block. */
const STACK_FOULS_PX = 330;
const STACK_STRIP_PX = 430;
/** …and the two-column one for the strip row under both columns. */
const TWO_STRIP_PX = 300;
/** One "Worst intervals" row (h-5 + gap), and what the strip above it takes
 *  in the three-column layout. */
const RANK_ROW_PX = 22;
const RANK_RESERVED_PX = 150;
/** Height of the fouls table (heading + 5 rows), and of the strip as a row. */
const FOULS_TABLE_PX = 150;
const STRIP_ROW_PX = 100;
const RANK_MAX = 8;

type SideLayout = 'fold' | 'stack' | 'two' | 'three';

/** Which column/row the pointer is on in a marginal histogram. */
type HistHover = { axis: 'col' | 'row'; i: number };

/** One face of a 10 px square in emphasis — the "Where it fouls" row glyph. */
const SIDE_GLYPH: Record<EnvelopeSide, [number, number, number, number]> = {
  left: [1.5, 1.5, 1.5, 10.5],
  right: [10.5, 1.5, 10.5, 10.5],
  roof: [1.5, 1.5, 10.5, 1.5],
  floor: [1.5, 10.5, 10.5, 10.5],
};

const SIDE_LABEL_KEY = {
  left: 'clearance.section.sideLeft',
  right: 'clearance.section.sideRight',
  roof: 'clearance.section.sideRoof',
  floor: 'clearance.section.sideFloor',
} as const satisfies Record<EnvelopeSide, string>;

/** Corridor-strip ink per interval tone (the K2 ladder, plus nothing). */
const TONE_FILL: Record<CorridorTone, string | null> = {
  violation: CLEARANCE_FILL.violation,
  nearMiss: CLEARANCE_FILL.nearMiss,
  clear: CLEARANCE_FILL.clear,
  empty: null,
};

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/** A height tick: whole metres stay whole, a 0.1/0.2/0.5 m step keeps its decimal. */
const heightTick = (v: number) =>
  formatFixed(v, Number.isInteger(v) ? 0 : Math.abs(v * 10 - Math.round(v * 10)) < 1e-6 ? 1 : 2);

/**
 * Section-local CSS: the focus ring the station section uses, and the
 * forced-colours fallbacks. The palette itself is `SV_TOKENS` on the dock root.
 */
const SECTION_CSS = `
.cl-section:focus-visible{outline:2px solid var(--sv-walk);outline-offset:2px;border-radius:4px;}
.cl-section-hover{pointer-events:none;}
@media (forced-colors:active){
  .cl-section-envelope{stroke:CanvasText;}
  .cl-section-worst circle,.cl-section-worst line{stroke:Highlight;}
}
`;

export interface ClearanceSectionChartProps {
  json: ClearanceJson;
  meta: ClearanceLayerMeta;
  batchId?: string;
  /** Which bin the dock is currently standing at (walk, else brush, else 0). */
  binIndex: number;
  /** Step the walk/selection to another bin — the dock owns that state. */
  onStepBin: (next: number) => void;
  width: number;
  height: number;
  /**
   * Holds the multi-megabyte fetch until the tab is actually on screen. The
   * dock only mounts this component on the Cross-section tab, so it defaults
   * to on; kept for a caller that mounts it hidden.
   */
  active?: boolean;
  /**
   * Where the dock's walk cursor stands (INTERNAL station), or null with no
   * walk. No longer read: it fed the removed "Violations only" stepping
   * (owner feedback, round 5), which had to step from the same place as the
   * dock's ◀ ▶ violation chevrons. Kept so the dock's call site compiles
   * unchanged; drop it together with the dock's prop.
   */
  cursorStation?: number | null;
  /** Rendered when the run carries no section grid (an older worker). */
  fallback: React.ReactNode;
  /**
   * "Look at cross-section" — the camera in front of the walked station,
   * facing the slice. The dock owns the camera and the walk, so it owns the
   * action; omitted, no button is drawn (and `L` does nothing).
   */
  onLookAt?: () => void;
  /** Why Look at cannot act right now (no walk), or null when it can. */
  lookAtReason?: string | null;
}

/**
 * One K2 rung's ink as the rgb triple `ImageData` wants, straight from the
 * live palette hex.
 *
 * Not read back from the DOM's `--sv-*`: `providers/index.tsx` flips the
 * `<html>` class in an effect of its own, and a child's effect runs BEFORE
 * its ancestors' — so on a theme flip a `getComputedStyle` read here would
 * still see the old theme and repaint the cells in the hues just left behind.
 * `useSvPalette` subscribes to the theme's cause, the same value that class
 * is written from.
 */
const paletteInk = (palette: SvPalette, key: keyof SvPalette): [number, number, number] => {
  const value = palette[key];
  const hex = typeof value === 'string' ? /^#([0-9a-f]{6})$/i.exec(value.trim()) : null;
  if (!hex) return [220, 70, 90];
  const n = parseInt(hex[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

/** The cell ink per state — the K2 ladder's own `CLEARANCE_FILL`. */
const STATE_FILL: Record<Exclude<SectionCellState, 'empty'>, string> = {
  violation: CLEARANCE_FILL.violation,
  nearMiss: CLEARANCE_FILL.nearMiss,
  clear: CLEARANCE_FILL.clear,
};

export const ClearanceSectionChart = ({
  json,
  meta,
  batchId,
  binIndex,
  onStepBin,
  width,
  height,
  active = true,
  fallback,
  onLookAt,
  lookAtReason = null,
}: ClearanceSectionChartProps) => {
  const { t } = useTranslation('project');
  const palette = useSvPalette();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const plotRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const [hover, setHover] = useState<{ col: number; row: number } | null>(null);
  const [box, setBox] = useState<{ w: number; h: number; bw: number } | null>(null);
  const [histHover, setHistHover] = useState<HistHover | null>(null);
  /** The "Where it fouls" row under the pointer: its edges drawn in emphasis. */
  const [sideHover, setSideHover] = useState<EnvelopeSide | null>(null);
  /** The corridor-strip interval under the pointer, for its read-out. */
  const [stripHover, setStripHover] = useState<number | null>(null);
  // Read only in pointer handlers, never during render (React Compiler).
  const stripDrag = useRef(false);
  // Same portal settings as the station section: the dock body is
  // `overflow-hidden`, so an in-flow tooltip would be clipped at its edge.
  const { containerRef, TooltipInPortal } = useTooltipInPortal({ scroll: true, detectBounds: true, zIndex: 9999 });
  const { tooltipLeft, tooltipTop, tooltipOpen, showTooltip, hideTooltip } = useTooltip<null>();

  const section = useClearanceSection(meta, batchId, active);
  const bins = section.data?.bins ?? null;

  const nBins = json.bins.length;
  const clampedIndex = clamp(binIndex, 0, Math.max(0, nBins - 1));
  const bin = json.bins[clampedIndex] ?? null;
  const bandsM = nearMissBandsOf(json.params);
  // ONE threshold (ruling P14): a cell is amber exactly where the verdict
  // goes MARGINAL. Resolved once here, not per cell inside the paint loop.
  const outerBandM = marginalBandM(bandsM);

  // Measure the space: the body row's WIDTH (less the side area's least
  // width, or all of it when the side area has folded above the plot) and the
  // plot's HEIGHT, which the flex column decides and the pane's width never
  // changes. Measured separately because the pane is then sized to its
  // drawing — see `plotW` — and measuring the pane's own width would feed
  // back into itself.
  useEffect(() => {
    const body = bodyRef.current;
    const plot = plotRef.current;
    if (!body || !plot || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      const bw = body.clientWidth;
      const h = plot.clientHeight;
      if (bw <= 0 || h <= 0) return;
      const w = bw < NARROW_PX ? bw : bw - PANEL_W - PANEL_GAP;
      setBox((prev) => (prev && prev.w === w && prev.h === h && prev.bw === bw ? prev : { w, h, bw }));
    });
    ro.observe(body);
    ro.observe(plot);
    return () => ro.disconnect();
  }, [bins]);

  // Before the first measurement (and in jsdom) the caller's size stands in.
  const bodyW = box?.bw ?? width;
  const folded = bodyW < NARROW_PX;
  const availW = Math.max(1, box?.w ?? (folded ? width : width - PANEL_W - PANEL_GAP));
  const plotH = Math.max(80, box?.h ?? height - CHROME_H);
  // The histograms are the first thing a small dock gives up (see the header).
  const showHist = !folded && plotH >= HIST_MIN_PLOT_H;
  const histRoom = showHist ? HIST_PX + HIST_GAP + 2 : 0;
  const margin = useMemo(
    // Without the histogram the axis title sits in the top margin, right over
    // the top tick's label (the "8" ran into "Height above datum"): give it
    // its own line there.
    () => ({ ...MARGIN, top: MARGIN.top + (histRoom || AXIS_TITLE_ROOM_PX), right: MARGIN.right + histRoom }),
    [histRoom],
  );
  // The pane is as wide as its 1:1 drawing needs (plus room for the
  // worst-point label beside the gauge), not the whole row: a flex-1 pane put
  // the stats panel ~700 px from the drawing it describes on a wide dock,
  // with nothing between them (audit §A5). The row's spare width lies to the
  // right of the panel instead, where it reads as margin, not as a hole.
  const plotW = bins
    ? Math.min(
        availW,
        Math.ceil(
          margin.left +
            margin.right +
            LABEL_ROOM_PX +
            (bins.nOffsets * bins.offsetStepM * Math.max(1, plotH - margin.top - margin.bottom)) /
              Math.max(bins.nHeights * bins.heightStepM, 1e-6),
        ),
      )
    : availW;

  // Pinned LEFT, not centred: the stats panel sits to the right, and a 1:1
  // gauge centred in a wide pane put a hole between the two.
  const view = useMemo(
    () => (bins ? sectionViewport(bins, plotW, plotH, margin, 'start') : null),
    [bins, plotW, plotH, margin],
  );

  const cells = useMemo(
    () => (section.data ? sectionCellsAt(section.data, clampedIndex) : null),
    [section.data, clampedIndex],
  );

  /** Signed distance to the gauge at every cell centre — once per run. */
  const field = useMemo(
    () => (bins ? profileDistanceField(bins, json.profile.vertices) : null),
    [bins, json.profile.vertices],
  );

  const srTable = useMemo(() => (bins && cells ? sectionColumnRows(bins, cells, SR_MAX_ROWS) : null), [bins, cells]);

  /** Which envelope side each cell centre faces — once per run, like `field`. */
  const sideField = useMemo(
    () => (bins ? profileSideField(bins, json.profile.vertices) : null),
    [bins, json.profile.vertices],
  );
  const sideEdges = useMemo(() => profileSideEdges(json.profile.vertices), [json.profile.vertices]);

  const worstAt = bin && bin.worstOffsetM !== null && bin.worstHeightM !== null ? bin : null;
  const fouls = useMemo(() => {
    if (!cells || !field || !sideField) return null;
    return foulsBySide(cells, field, sideField, outerBandM, {
      side: worstAt ? profileOutwardSide(json.profile.vertices, worstAt.worstOffsetM!, worstAt.worstHeightM!) : null,
      depthM: worstAt?.maxIntrusionM ?? null,
    });
  }, [cells, field, sideField, outerBandM, worstAt, json.profile.vertices]);

  const marginals = useMemo(
    () => (bins && cells && field ? sectionMarginals(cells, field, outerBandM, bins.nOffsets, bins.nHeights) : null),
    [bins, cells, field, outerBandM],
  );

  const strip = useMemo(() => corridorBuckets(json.bins, STRIP_MAX_BARS), [json.bins]);
  const stripCounts = useMemo(() => corridorCounts(json.bins), [json.bins]);
  const stripPeak = useMemo(() => strip.reduce((m, b) => Math.max(m, b.violations), 0), [strip]);

  // ---------------------------------------------------------------- the grid
  //
  // Drawn at GRID resolution into an offscreen canvas and blitted scaled, with
  // smoothing off. Smoothing would interpolate neighbouring cells into each
  // other, which on a density plot invents returns in empty space — the one
  // thing a clearance drawing must never do.
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv || !bins || !cells || !view || !field) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;

    const dpr = Math.min(3, Math.max(1, window.devicePixelRatio || 1));
    cv.width = Math.max(1, Math.round(plotW * dpr));
    cv.height = Math.max(1, Math.round(plotH * dpr));
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, plotW, plotH);

    const peak = peakCount(cells.count);
    if (peak <= 0) return;

    // The K2 ladder's three inks, from the live palette — which is in this
    // effect's deps, so a theme flip repaints the cells in the same hues as
    // the chart's bars instead of leaving the old theme's on the canvas.
    const ink = {
      violation: paletteInk(palette, CLEARANCE_K2_KEY.violation),
      nearMiss: paletteInk(palette, CLEARANCE_K2_KEY.nearMiss),
      clear: paletteInk(palette, CLEARANCE_K2_KEY.clear),
    };

    const { nOffsets, nHeights } = bins;
    const off = document.createElement('canvas');
    off.width = nOffsets;
    off.height = nHeights;
    const octx = off.getContext('2d');
    if (!octx) return;
    const img = octx.createImageData(nOffsets, nHeights);
    for (let row = 0; row < nHeights; row++) {
      // Row 0 is the LOWEST height; ImageData row 0 is the top of the picture.
      const imgRow = nHeights - 1 - row;
      for (let col = 0; col < nOffsets; col++) {
        const src = row * nOffsets + col;
        const total = cells.count[src];
        if (total === 0) continue;
        const bad = cells.violations[src];
        const state = sectionCellState(total, bad, field[src], outerBandM);
        if (state === 'empty') continue;
        const a = density(total, peak);
        const c = ink[state];
        // A cell that is only PARTLY violating still reads as a violation:
        // the question the section answers is "is anything of mine in the
        // gauge here", and averaging it into the context grey would hide a
        // single intruding return behind a hundred clear ones. Near-miss
        // cells get the same floor for the same reason one rung down.
        const px = (imgRow * nOffsets + col) * 4;
        img.data[px] = c[0];
        img.data[px + 1] = c[1];
        img.data[px + 2] = c[2];
        img.data[px + 3] = Math.round(255 * (state === 'clear' ? a * 0.85 : Math.max(0.55, a)));
      }
    }
    octx.putImageData(img, 0, 0);
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(off, view.originX, view.originY, view.width, view.height);
  }, [bins, cells, view, field, outerBandM, palette, plotW, plotH]);

  // ------------------------------------------------------------ interaction
  const stepBy = useCallback(
    (delta: number) => onStepBin(clamp(binIndex + delta, 0, nBins - 1)),
    [binIndex, nBins, onStepBin],
  );

  /** The interval keys, shared by the pane and the corridor strip so the two
   *  are one muscle memory (and the station dock's section's): arrows step a
   *  bin, Shift steps ten, Home/End jump to the ends. Returns whether the key
   *  was one of them. */
  const stepByKey = useCallback(
    (e: KeyboardEvent<HTMLElement>): boolean => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        stepBy((e.key === 'ArrowRight' ? 1 : -1) * (e.shiftKey ? 10 : 1));
        return true;
      }
      if (e.key === 'Home' || e.key === 'End') {
        e.preventDefault();
        onStepBin(e.key === 'Home' ? 0 : nBins - 1);
        return true;
      }
      return false;
    },
    [nBins, onStepBin, stepBy],
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      // `L` looks at the section, as on the station dock's pane.
      if ((e.key === 'l' || e.key === 'L') && onLookAt) {
        e.preventDefault();
        e.stopPropagation();
        if (!lookAtReason) onLookAt();
        return;
      }
      stepByKey(e);
    },
    [lookAtReason, onLookAt, stepByKey],
  );

  // ---- corridor strip: press or drag anywhere on it to stand on that interval
  const stripIndexAt = (e: PointerEvent<HTMLDivElement>): number => {
    const rect = e.currentTarget.getBoundingClientRect();
    return binAtFraction(nBins, rect.width > 0 ? (e.clientX - rect.left) / rect.width : 0);
  };
  const onStripDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    stripDrag.current = true;
    // Capture so a drag that leaves the strip keeps walking it.
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const i = stripIndexAt(e);
    if (i !== clampedIndex) onStepBin(i);
  };
  const onStripMove = (e: PointerEvent<HTMLDivElement>) => {
    const i = stripIndexAt(e);
    if (i !== stripHover) setStripHover(i);
    if (stripDrag.current && i !== clampedIndex) onStepBin(i);
  };
  const onStripUp = () => {
    stripDrag.current = false;
  };

  const setPlotRef = useCallback(
    (el: HTMLDivElement | null) => {
      plotRef.current = el;
      containerRef(el);
    },
    [containerRef],
  );

  const onOverlayMove = (e: PointerEvent<SVGRectElement>) => {
    if (!bins || !view) return;
    // `currentTarget` is the hit rect over the drawn grid, so this is the
    // pointer's position within the GRID regardless of scroll or the dock's
    // `overflow-hidden` ancestor.
    const rect = e.currentTarget.getBoundingClientRect();
    const x = clamp(e.clientX - rect.left, 0, view.width);
    const y = clamp(e.clientY - rect.top, 0, view.height);
    const col = colAt(bins, bins.offsetStartM + x / view.scale);
    const row = rowAt(bins, bins.heightStartM + (view.height - y) / view.scale);
    if (col < 0 || col >= bins.nOffsets || row < 0 || row >= bins.nHeights) {
      onOverlayLeave();
      return;
    }
    if (!hover || hover.col !== col || hover.row !== row) setHover({ col, row });
    showTooltip({ tooltipData: null, tooltipLeft: view.originX + x + 12, tooltipTop: view.originY + y + 12 });
  };

  const onOverlayLeave = () => {
    setHover(null);
    hideTooltip();
  };

  if (section.unavailable) return <>{fallback}</>;

  if (section.isLoading || section.error) {
    return (
      <div className='flex h-full items-start' data-testid='clearance-section-status'>
        <LoadingBadgeKeyframes />
        <LoadingBadge
          name={t('clearance.section.title')}
          phase={section.error ? 'failed' : 'fetching'}
          progress={
            section.progress?.totalBytes ? section.progress.loadedBytes / section.progress.totalBytes : undefined
          }
          loadedBytes={section.progress?.loadedBytes}
          totalBytes={section.progress?.totalBytes ?? undefined}
          error={section.error?.message}
          labels={{
            fetching: t('clearance.section.loading'),
            decoding: t('clearance.section.loading'),
            failed: t('clearance.section.failed'),
          }}
        />
      </div>
    );
  }

  if (!bins || !view || !cells || !bin || !field || !srTable) return <>{fallback}</>;

  // ------------------------------------------------------------ scales
  const offsetEndM = bins.offsetStartM + bins.nOffsets * bins.offsetStepM;
  const heightEndM = bins.heightStartM + bins.nHeights * bins.heightStepM;
  const xScale = scaleLinear<number>({
    domain: [bins.offsetStartM, offsetEndM],
    range: [view.originX, view.originX + view.width],
  });
  const yScale = scaleLinear<number>({
    domain: [bins.heightStartM, heightEndM],
    range: [view.originY + view.height, view.originY],
  });
  // Ticks from the SCALE, which is the same on both axes: a metre is the same
  // number of pixels across as up, so the grid squares are squares.
  // Closer than the default 56 px on a SMALL drawing: on the 280 px dock a
  // gauge is ~15 px/m, where 56 px picked a 5 m step — one labelled tick on
  // each axis. 28 px there gives a 2 m step; a large drawing keeps 48.
  const tickGap = view.scale < 30 ? 28 : 48;
  const xTicks = sectionTicks(bins.offsetStartM, offsetEndM - bins.offsetStartM, view.scale, tickGap);
  const yTicks = sectionTicks(bins.heightStartM, heightEndM - bins.heightStartM, view.scale, tickGap);
  const plotBottom = view.originY + view.height;
  const plotRight = view.originX + view.width;
  const datumY = toPxY(view, bins, 0);
  const datumOnPlot = datumY >= view.originY - 0.5 && datumY <= plotBottom + 0.5;
  const envelopePoints = profilePolygonPoints(json.profile.vertices, view, bins);
  const worst =
    bin.worstOffsetM !== null && bin.worstHeightM !== null
      ? { x: toPxX(view, bins, bin.worstOffsetM), y: toPxY(view, bins, bin.worstHeightM) }
      : null;
  const centreOnAxis = bins.offsetStartM <= 0 && offsetEndM >= 0;
  // The worst-point label goes OUTSIDE the envelope and its near-miss halo,
  // on the point's own side, with a leader line back to the ring.
  const worstLabelText =
    bin.maxIntrusionM !== null ? t('clearance.section.binWorst', { depth: formatFixed(bin.maxIntrusionM, 3) }) : '';
  const envOffsets = json.profile.vertices.map(([o]) => o);
  const haloPx = outerBandM * view.scale;
  const worstLabel = worst
    ? worstLabelPlacement({
        markerX: worst.x,
        markerY: worst.y,
        envLeftPx: toPxX(view, bins, Math.min(...envOffsets)) - haloPx,
        envRightPx: toPxX(view, bins, Math.max(...envOffsets)) + haloPx,
        labelW: worstLabelText.length * LABEL_CHAR_PX,
        // The SVG spans the whole plot box, so the label may sit in the
        // spare width right of the grid — that is what the space is for —
        // unless the height histogram stands there.
        minX: 2,
        maxX: showHist ? plotRight - 2 : plotW - 2,
        top: view.originY,
        bottom: plotBottom,
      })
    : null;

  // ------------------------------------------------------------ copy
  const rangeText = formatStationRange(
    { staStartDisplayM: bin.staStartDisplayM, staEndDisplayM: bin.staEndDisplayM },
    2,
  );
  const verdict = clearanceVerdict({
    violationCount: bin.violationCount,
    scoredCount: bin.pointCount,
    nearMiss: bin.nearMiss,
  });
  const atStart = clampedIndex <= 0;
  const atEnd = clampedIndex >= nBins - 1;

  const hoverIdx = hover ? hover.row * bins.nOffsets + hover.col : -1;
  const hoverState: SectionCellState | null =
    hoverIdx >= 0
      ? sectionCellState(cells.count[hoverIdx], cells.violations[hoverIdx], field[hoverIdx], outerBandM)
      : null;
  const hoverDist = hoverIdx >= 0 ? field[hoverIdx] : 0;
  const stateText = (s: SectionCellState): string => {
    switch (s) {
      case 'violation':
        return t('clearance.section.stateViolation');
      case 'nearMiss':
        return t('clearance.section.stateNearMiss', {
          band: formatFixed(nearMissBandFor(hoverDist, bandsM) ?? outerBandM, 2),
        });
      case 'clear':
        return t('clearance.section.stateClear');
      default:
        return t('clearance.section.stateEmpty');
    }
  };
  const tipRows =
    hover && hoverState
      ? [
          {
            label: t('clearance.section.tipState'),
            value: stateText(hoverState),
            ink: hoverState === 'empty' ? undefined : STATE_FILL[hoverState],
          },
          { label: t('clearance.section.tipPoints'), value: cells.count[hoverIdx].toLocaleString() },
          { label: t('clearance.section.tipViolating'), value: cells.violations[hoverIdx].toLocaleString() },
          {
            label: t('clearance.section.tipMargin'),
            // A cell with nothing in it has no margin to report: the
            // geometry is there, the evidence is not.
            value:
              hoverState === 'empty'
                ? '—'
                : hoverDist < 0
                  ? t('clearance.section.marginInside', { value: formatFixed(-hoverDist, 3) })
                  : t('clearance.section.marginOutside', { value: formatFixed(hoverDist, 3) }),
          },
        ]
      : [];

  /** One side of the interval stepper: the adjacent interval, inert at the
   *  run's end. */
  const stepSide = (dir: -1 | 1, testId: string) => ({
    testId,
    label: t(dir === -1 ? 'clearance.section.prev' : 'clearance.section.next'),
    disabled: dir === -1 ? atStart : atEnd,
    onClick: () => stepBy(dir),
  });

  const verdictLabel = t(`expectation.verdict.${verdict}`);
  const violationsText = t('clearance.section.binViolations', {
    count: bin.violationCount,
    formatted: bin.violationCount.toLocaleString(),
  });
  /** What a screen reader hears when the section is stepped (finding #10):
   *  the stepper's read-out is a polite live region, and this rides in it. */
  const announceText = t('clearance.section.announce', {
    range: rangeText,
    n: (clampedIndex + 1).toLocaleString(),
    total: nBins.toLocaleString(),
    verdict: verdictLabel,
    violations: violationsText,
  });

  // Every rung of the near-miss ladder, innermost first, against its band
  // (`NEAR_MISS_RUNGS` ↔ `nearMissBandsM`, the table's pairing).
  const nearRows = NEAR_MISS_RUNGS.map((k, i) => ({
    key: k,
    band: bandsM[i] ?? bandsM[bandsM.length - 1],
    n: bin.nearMiss[k],
    outer: i === NEAR_MISS_RUNGS.length - 1,
  }));

  const legendItems: DockLegendItem[] = [
    ...CLEARANCE_LEGEND_ORDER.map((k) => ({
      key: k,
      testId: `clearance-section-legend-${k}`,
      color: STATE_FILL[k],
      // CELL states, not interval verdicts: a cell is inside, near or clear —
      // the chart's Fail / Marginal / Pass would judge a single cell. Amber
      // names the outermost band it is drawn within.
      label:
        k === 'nearMiss'
          ? t('clearance.section.legendNearMiss', { band: formatFixed(outerBandM, 2) })
          : k === 'violation'
            ? t('clearance.section.stateViolation')
            : t('clearance.section.stateClear'),
    })),
    {
      key: 'envelope',
      label: t('clearance.section.legendEnvelope'),
      swatch: (
        <svg width={12} height={12} aria-hidden className='shrink-0 text-slate-900 dark:text-stone-100'>
          <rect x={1.5} y={1.5} width={9} height={9} fill='none' stroke='currentColor' strokeWidth={1.5} />
        </svg>
      ),
    },
    {
      key: 'worst',
      label: t('clearance.section.legendWorst'),
      swatch: (
        <svg width={12} height={12} aria-hidden className='shrink-0'>
          <circle cx={6} cy={6} r={4} fill='none' stroke={CLEARANCE_FILL.violation} strokeWidth={1.5} />
        </svg>
      ),
    },
    { key: 'datum', label: t('clearance.section.legendDatum'), shape: 'dashed' },
    {
      key: 'density',
      label: t('clearance.section.legendDensity'),
      title: t('clearance.section.legendDensityHelp'),
      // A three-step ramp of the context ink: darker = more points.
      swatch: (
        <svg width={12} height={12} aria-hidden className='shrink-0'>
          {[0.25, 0.55, 0.9].map((a, i) => (
            <rect key={a} x={i * 4} y={1} width={4} height={10} fill={CLEARANCE_FILL.clear} fillOpacity={a} />
          ))}
        </svg>
      ),
    },
  ];

  // A dock too SHORT for the whole panel (the 280 px default leaves ~150 px
  // beside the plot) folds the near-miss ladder to its outer rung and drops
  // the cell note, rather than scrolling the figures a reader came for. Height
  // has no container query here, so this one is measured.
  const short = box ? plotH < SHORT_PANEL_PX : false;

  // ------------------------------------------------------------ side area
  //
  // Measured, not container-queried: the area's width is the body's less the
  // drawing's, and the drawing's width is itself computed from the plot's
  // height — a nested `@container` would also re-root the figures' own
  // `@max-[700px]` fold onto the side area instead of the pane.
  const sideW = Math.max(0, bodyW - plotW - PANEL_GAP);
  const layout: SideLayout = folded
    ? 'fold'
    : sideW >= SIDE_THREE_PX
      ? 'three'
      : sideW >= SIDE_TWO_PX
        ? 'two'
        : 'stack';
  // Short docks give up the strip before "Where it fouls" — unless there is a
  // third column for it to stand in, where it costs no height at all.
  const showFouls = layout === 'two' || layout === 'three' || (layout === 'stack' && plotH >= STACK_FOULS_PX);
  const showStrip =
    layout === 'three' || (layout === 'two' && plotH >= TWO_STRIP_PX) || (layout === 'stack' && plotH >= STACK_STRIP_PX);
  // Three columns on a tall dock leave the strip's column mostly empty below
  // it: the run's worst intervals go there, as many as fit without a scroll.
  const rankRoom =
    layout === 'three'
      ? plotH - RANK_RESERVED_PX
      : layout === 'two'
        ? plotH - FOULS_TABLE_PX - (showStrip ? STRIP_ROW_PX : 0)
        : 0;
  // Less the list's own heading and top margin.
  const rankRows = clamp(Math.floor((rankRoom - 26) / RANK_ROW_PX), 0, RANK_MAX);
  const ranked = rankRows > 0 ? rankIntervals(json.bins, bandsM, rankRows) : [];
  // Rough width of the fouls column, to drop its least-quoted figure
  // (near-miss points: the histograms and the ladder have them) when narrow.
  const foulsW =
    layout === 'stack' ? sideW - 12 : layout === 'two' ? sideW - 12 - (sideW < 600 ? 192 : 240) - 32 : 448;
  const foulsCompact = foulsW < 330;
  // Narrower still: the count goes too, keeping the two figures a foul is
  // quoted by — how deep, and how close.
  const foulsMinimal = foulsW < 290;
  // A near-miss side is only emphasised when the WORKER saw a near miss here:
  // the per-side figures come from cell centres (± half a cell), and amber on
  // a row of an interval the badge calls Pass would contradict the badge.
  const foulCandidate = fouls ? worstSide(fouls) : null;
  const worstFoul = foulCandidate?.kind === 'nearMiss' && verdict === 'PASS' ? null : foulCandidate;
  const sideInk = (kind: 'violation' | 'nearMiss') =>
    kind === 'violation' ? CLEARANCE_FILL.violation : CLEARANCE_FILL.nearMiss;

  // ------------------------------------------------------------ histograms
  const colPx = bins.offsetStepM * view.scale;
  const rowPx = bins.heightStepM * view.scale;
  const histBaseY = view.originY - HIST_GAP;
  const histBaseX = plotRight + HIST_GAP;
  /**
   * A stacked bar's two lengths in px, on the section's LOG curve
   * (`density`): the floor's ballast is a hundred thousand near-miss points
   * and a branch through the wall a few thousand violating ones, and on a
   * linear scale the branch — the reason to look — is a hairline. The red
   * part is the log of the violating count on its own (it is what the
   * histogram is for); amber fills the bar to the log of the total. A
   * non-zero part is never thinner than 1.5 px.
   */
  const histLens = (v: number, n: number, peak: number): [number, number] => {
    if (peak <= 0) return [0, 0];
    const lv = v > 0 ? Math.max(1.5, density(v, peak) * HIST_PX) : 0;
    const total = Math.max(lv, density(v + n, peak) * HIST_PX);
    return [lv, n > 0 ? Math.max(1.5, total - lv) : 0];
  };
  const onHistMove = (axis: 'col' | 'row') => (e: PointerEvent<SVGRectElement>) => {
    if (!marginals) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const i =
      axis === 'col'
        ? clamp(Math.floor((e.clientX - rect.left) / Math.max(colPx, 1e-6)), 0, bins.nOffsets - 1)
        : clamp(bins.nHeights - 1 - Math.floor((e.clientY - rect.top) / Math.max(rowPx, 1e-6)), 0, bins.nHeights - 1);
    if (!histHover || histHover.axis !== axis || histHover.i !== i) setHistHover({ axis, i });
    setHover(null);
    showTooltip({
      tooltipData: null,
      tooltipLeft: axis === 'col' ? view.originX + (i + 1) * colPx + 8 : histBaseX + HIST_PX + 8,
      tooltipTop: axis === 'col' ? histBaseY + 8 : view.originY + (bins.nHeights - i) * rowPx - 8,
    });
  };
  const onHistLeave = () => {
    setHistHover(null);
    hideTooltip();
  };
  const histTip =
    histHover && marginals
      ? {
          title:
            histHover.axis === 'col'
              ? t('clearance.section.histTipOffset', {
                  from: formatSigned(bins.offsetStartM + histHover.i * bins.offsetStepM, 2),
                  to: formatSigned(bins.offsetStartM + (histHover.i + 1) * bins.offsetStepM, 2),
                })
              : t('clearance.section.histTipHeight', {
                  from: formatFixed(bins.heightStartM + histHover.i * bins.heightStepM, 2),
                  to: formatFixed(bins.heightStartM + (histHover.i + 1) * bins.heightStepM, 2),
                }),
          rows: [
            {
              label: t('clearance.section.tipViolating'),
              value: (histHover.axis === 'col' ? marginals.colViolations : marginals.rowViolations)[
                histHover.i
              ].toLocaleString(),
              ink: CLEARANCE_FILL.violation,
            },
            {
              label: t('clearance.section.tipNearMiss', { band: formatFixed(outerBandM, 2) }),
              value: (histHover.axis === 'col' ? marginals.colNearMiss : marginals.rowNearMiss)[
                histHover.i
              ].toLocaleString(),
              ink: CLEARANCE_FILL.nearMiss,
            },
          ],
        }
      : null;

  // ------------------------------------------------------------ corridor strip
  const stripBucketOf = (i: number) => {
    const k = strip.findIndex((b) => i >= b.from && i <= b.to);
    return k < 0 ? 0 : k;
  };
  const stripCurrent = stripBucketOf(clampedIndex);
  const stripHoverBucket = stripHover !== null ? stripBucketOf(stripHover) : null;
  const stripHoverBin = stripHover !== null ? (json.bins[stripHover] ?? null) : null;
  const stripReadout = stripHoverBin
    ? t('clearance.section.corridorHover', {
        range: formatStationRange(stripHoverBin, 2),
        violations: t('clearance.section.binViolations', {
          count: stripHoverBin.violationCount,
          formatted: stripHoverBin.violationCount.toLocaleString(),
        }),
      })
    : stripCounts.violating > 0
      ? t('clearance.section.corridorSummary', {
          count: stripCounts.violating,
          formatted: stripCounts.violating.toLocaleString(),
          total: nBins.toLocaleString(),
        })
      : stripCounts.marginal > 0
        ? t('clearance.section.corridorMarginal', {
            count: stripCounts.marginal,
            formatted: stripCounts.marginal.toLocaleString(),
            total: nBins.toLocaleString(),
          })
        : t('clearance.section.corridorClean');
  // Bars a hair narrower than their slot while they are wide enough to show
  // a gap, so neighbouring intervals stay countable.
  const barInset = strip.length <= 160 ? 0.12 : 0;
  const firstBin = json.bins[0];
  const lastBin = json.bins[nBins - 1];

  /** The run's worst intervals, as many as fit: under the strip in three
   *  columns, under "Where it fouls" in two. */
  const rankedBlock = ranked.length > 0 && (
    // Capped: in the three-column layout the strip's column can be ~600 px,
    // and a row that wide reads its range and its depth as two lists.
    <div data-testid='clearance-section-ranked' className='mt-2 flex min-h-0 max-w-sm flex-col gap-0.5'>
      <h4
        className={cn(DOCK_TEXT.metaMuted, 'h-4 shrink-0 font-normal uppercase')}
        title={t('clearance.section.rankedHelp')}
      >
        {t('clearance.section.ranked')}
      </h4>
      <ol className='flex flex-col gap-0.5'>
        {ranked.map((r, k) => {
          const b = json.bins[r.index];
          const here = r.index === clampedIndex;
          return (
            <li key={r.index}>
              <button
                type='button'
                data-testid={`clearance-section-ranked-${k}`}
                aria-current={here || undefined}
                onClick={() => onStepBin(r.index)}
                className={cn(
                  DOCK_CONTROL.focus,
                  'flex h-5 w-full items-center gap-2 rounded px-1.5 text-left text-[11px] leading-4 hover:bg-slate-100 dark:hover:bg-stone-800',
                )}
                style={
                  here
                    ? {
                        background: 'color-mix(in srgb, var(--sv-walk) 10%, transparent)',
                        boxShadow: 'inset 2px 0 0 var(--sv-walk)',
                      }
                    : undefined
                }
              >
                <span
                  aria-hidden
                  className='size-2 shrink-0 rounded-[2px]'
                  style={{ background: r.kind === 'violation' ? CLEARANCE_FILL.violation : CLEARANCE_FILL.nearMiss }}
                />
                <span className='sv-num min-w-0 flex-1 truncate'>{formatStationRange(b, 0)}</span>
                {r.kind === 'violation' ? (
                  <>
                    {!foulsCompact && (
                      <span className='sv-num w-16 shrink-0 text-right opacity-70'>{r.count.toLocaleString()}</span>
                    )}
                    <span
                      className='sv-num w-14 shrink-0 text-right font-semibold'
                      style={{ color: CLEARANCE_FILL.violation }}
                    >
                      {formatFixed(b.maxIntrusionM ?? 0, 3)}
                    </span>
                  </>
                ) : (
                  <span className='sv-num shrink-0 text-right opacity-80'>
                    {t('clearance.section.rankedNear', {
                      band: formatFixed(r.bandM ?? outerBandM, 2),
                      points: r.count.toLocaleString(),
                    })}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );

  return (
    <div className='flex h-full min-h-0 w-full flex-col gap-2 text-xs text-slate-700 @container dark:text-stone-300'>
      <style>{SECTION_CSS}</style>

      {/* ---- Top row: the interval stepper, the legend, Look at ---- */}
      {/* Below 560 px (a phone) the row wraps: stepper + Look at on the first
          line, the legend on its own below — in one row the legend was
          squeezed to nothing and Look at ran off the dock. */}
      <div className='flex h-7 shrink-0 items-center gap-2 whitespace-nowrap @max-[560px]:h-auto @max-[560px]:flex-wrap @max-[560px]:gap-y-1.5'>
        <DockStepper
          testId='clearance-section-stepper'
          ariaLabel={t('clearance.section.stepper')}
          prev={stepSide(-1, 'clearance-section-prev')}
          next={stepSide(1, 'clearance-section-next')}
          center={{
            testId: 'clearance-section-position',
            mono: true,
            title: t('clearance.section.binOf', {
              n: (clampedIndex + 1).toLocaleString(),
              total: nBins.toLocaleString(),
            }),
            // The read-out is the stepper's polite live region; the sentence
            // after the range is for the ear only.
            label: (
              <>
                <span data-testid='clearance-section-station' className='font-semibold'>
                  {rangeText}
                </span>
                <span id={`${uid}-announce`} className='sr-only' data-testid='clearance-section-announce'>
                  {announceText}
                </span>
              </>
            ),
          }}
        />
        <DockChartLegend
          testId='clearance-section-legend'
          className='ml-2 min-w-0 flex-1 @max-[560px]:order-last @max-[560px]:ml-0 @max-[560px]:basis-full'
          items={legendItems}
          right={
            <span
              role='img'
              data-testid='clearance-section-hint'
              aria-label={`${t('clearance.section.hintLabel')}: ${t('clearance.section.hint')}`}
              title={t('clearance.section.hint')}
              className='inline-flex cursor-help items-center text-slate-500 dark:text-stone-400'
            >
              <Info className={DOCK_CONTROL.iconSm} aria-hidden />
            </span>
          }
        />
        {/* Right end of the row, as on the station dock's Cross-section:
            the camera action is about the STATION, not the drawing.
            `aria-disabled` with the reason as its title while there is no
            walk — never the HTML attribute, which would hide that reason. */}
        {onLookAt && (
          <button
            type='button'
            data-testid='clearance-look-at-section'
            aria-disabled={lookAtReason ? true : undefined}
            title={lookAtReason ?? t('stationVolumes.section.lookAtHelp')}
            onClick={() => !lookAtReason && onLookAt()}
            className={cn(
              DOCK_CONTROL.button,
              '@max-[560px]:ml-auto',
              lookAtReason && cn(DOCK_CONTROL.inert, 'hover:bg-transparent dark:hover:bg-transparent'),
            )}
          >
            {/* An icon on a phone, so it fits beside the stepper; the label
                stays for screen readers (and the hotkey hint is moot there). */}
            <Video aria-hidden className={cn(DOCK_CONTROL.iconSm, 'hidden @max-[560px]:block')} />
            <span className='@max-[560px]:sr-only'>{t('stationVolumes.section.lookAt')}</span>
          </button>
        )}
      </div>

      {/* ---- Body: the drawing, and the interval's figures beside it ---- */}
      <div ref={bodyRef} className='flex min-h-0 flex-1 gap-3 @max-[700px]:flex-col @max-[700px]:gap-1.5'>
        {/* ---- The pane: one tab stop, arrow-driven ---- */}
        <div
          data-testid='clearance-section'
          role='application'
          tabIndex={0}
          aria-label={t('clearance.section.title')}
          aria-describedby={`${uid}-announce`}
          onKeyDown={onKeyDown}
          className='cl-section flex min-h-0 min-w-0 flex-1 flex-col outline-none'
          // Side by side, the pane is EXACTLY the drawing's width (`plotW`):
          // as a capped `flex-1` it split the row with the side area, so a
          // tall (full-screen) 1:1 drawing wider than half the row spilled its
          // absolutely-positioned canvas under the side area, and `sideW` —
          // computed from `plotW` — laid the side area out for a width it did
          // not have. Folded (column layout) it stays a cap: the pane must
          // still grow DOWN, which `flex: none` would stop.
          style={box ? (folded ? { maxWidth: plotW } : { flex: 'none', width: plotW }) : undefined}
        >
          <div ref={setPlotRef} className='relative min-h-0 w-full flex-1'>
            {/* Grid first, under the canvas: a reference frame, not content. */}
            <svg width={plotW} height={plotH} className='absolute inset-0' aria-hidden>
              {yTicks.map((v) => (
                <line
                  key={`gy${v}`}
                  x1={view.originX}
                  x2={plotRight}
                  y1={yScale(v)}
                  y2={yScale(v)}
                  stroke='var(--sv-grid)'
                />
              ))}
              {xTicks.map((v) => (
                <line
                  key={`gx${v}`}
                  x1={xScale(v)}
                  x2={xScale(v)}
                  y1={view.originY}
                  y2={plotBottom}
                  stroke='var(--sv-grid)'
                />
              ))}
            </svg>
            <canvas
              ref={canvasRef}
              data-testid='clearance-section-canvas'
              className='absolute inset-0'
              style={{ width: plotW, height: plotH }}
            />
            <svg
              data-testid='clearance-section-svg'
              width={plotW}
              height={plotH}
              className='absolute inset-0'
              role='img'
              aria-label={t('clearance.section.alt', {
                range: rangeText,
                count: bin.violationCount,
                formatted: bin.violationCount.toLocaleString(),
              })}
              data-px-per-metre={view.scale}
            >
              <defs>
                {/* Everything OUTSIDE the gauge: the near-miss ladder is a
                  clearance margin, so it must never tint the gauge itself. */}
                <mask id={`${uid}-outside`} maskUnits='userSpaceOnUse' x={0} y={0} width={plotW} height={plotH}>
                  <rect x={0} y={0} width={plotW} height={plotH} fill='white' />
                  <polygon points={envelopePoints} fill='black' />
                </mask>
                <clipPath id={`${uid}-plot`}>
                  <rect x={view.originX} y={view.originY} width={view.width} height={view.height} />
                </clipPath>
              </defs>

              {/* The near-miss ladder as a halo. A stroke of width 2·b with
                round joins, masked to the outside, is EXACTLY the set of
                points within b of the outline — the Euclidean band the
                worker measures — without offsetting a polygon by hand. One
                stroke per band, so the rungs darken toward the gauge. */}
              <g data-testid='clearance-section-band' mask={`url(#${uid}-outside)`} clipPath={`url(#${uid}-plot)`}>
                {[...bandsM]
                  .sort((a, b) => b - a)
                  .map((b) => (
                    <polygon
                      key={b}
                      points={envelopePoints}
                      fill='none'
                      stroke={CLEARANCE_FILL.nearMiss}
                      strokeOpacity={0.14}
                      strokeWidth={2 * b * view.scale}
                      strokeLinejoin='round'
                    />
                  ))}
              </g>

              {/* The datum: height 0, i.e. the alignment plus the run's vertical
                offset. Emphasised because it is the line every height on this
                drawing is measured from, and because a road run's whole
                question is how far the envelope's floor sits above it. */}
              {datumOnPlot && (
                <line
                  data-testid='clearance-section-datum'
                  x1={view.originX}
                  x2={plotRight}
                  y1={datumY}
                  y2={datumY}
                  stroke='currentColor'
                  strokeWidth={1}
                  strokeDasharray='4 3'
                  opacity={0.6}
                />
              )}

              {/* The gauge. Stroked in the foreground ink, barely filled: the
                cloud behind it is the subject, and a solid gauge would hide
                the returns it contains. Not a K2 colour — it is geometry, the
                thing every cell is judged against, not a judgement. */}
              <polygon
                data-testid='clearance-section-envelope'
                className='cl-section-envelope text-slate-900 dark:text-stone-100'
                points={envelopePoints}
                fill='currentColor'
                fillOpacity={0.04}
                stroke='currentColor'
                strokeWidth={1.75}
                strokeLinejoin='round'
              />

              {/* Centreline tick in the walk cursor's colour, as on the station
                section: this drawing IS the walked station. */}
              <line
                data-testid='clearance-section-centreline'
                x1={xScale(0)}
                x2={xScale(0)}
                y1={plotBottom - 8}
                y2={plotBottom}
                stroke='var(--sv-walk)'
                strokeWidth={2}
              />

              {worst && (
                <g data-testid='clearance-section-worst' className='cl-section-worst'>
                  {/* A surface-coloured halo under the ring, so the marker still
                    reads on top of the violation cells it sits in. */}
                  <circle
                    cx={worst.x}
                    cy={worst.y}
                    r={7}
                    fill='none'
                    stroke='var(--sv-surface, #fff)'
                    strokeWidth={4}
                  />
                  <circle
                    cx={worst.x}
                    cy={worst.y}
                    r={7}
                    fill='none'
                    stroke={CLEARANCE_FILL.violation}
                    strokeWidth={2}
                  />
                  <line
                    x1={worst.x - 11}
                    x2={worst.x + 11}
                    y1={worst.y}
                    y2={worst.y}
                    stroke={CLEARANCE_FILL.violation}
                    strokeWidth={1}
                  />
                  <line
                    x1={worst.x}
                    x2={worst.x}
                    y1={worst.y - 11}
                    y2={worst.y + 11}
                    stroke={CLEARANCE_FILL.violation}
                    strokeWidth={1}
                  />
                  {worstLabel && bin.maxIntrusionM !== null && (
                    <>
                      {/* The leader: the label sits outside the envelope and its
                        halo, so a thin line says which ring it is about. */}
                      {worstLabel.leader && (
                        <line
                          data-testid='clearance-section-worst-leader'
                          {...worstLabel.leader}
                          stroke={CLEARANCE_FILL.violation}
                          strokeWidth={1}
                          opacity={0.8}
                        />
                      )}
                      <text
                        data-testid='clearance-section-worst-label'
                        data-side={worstLabel.side}
                        x={worstLabel.x}
                        y={worstLabel.y}
                        textAnchor={worstLabel.anchor}
                        fontSize={TICK_FONT}
                        fontWeight={600}
                        className='sv-num'
                        fill={CLEARANCE_FILL.violation}
                        stroke='var(--sv-surface, #fff)'
                        strokeWidth={3}
                        paintOrder='stroke'
                      >
                        {worstLabelText}
                      </text>
                    </>
                  )}
                </g>
              )}

              {hover && hoverIdx >= 0 && (
                <rect
                  data-testid='clearance-section-hover'
                  className='cl-section-hover text-slate-900 dark:text-stone-100'
                  x={toPxX(view, bins, offsetAt(bins, hover.col) - bins.offsetStepM / 2)}
                  y={toPxY(view, bins, heightAt(bins, hover.row) + bins.heightStepM / 2)}
                  width={Math.max(2, bins.offsetStepM * view.scale)}
                  height={Math.max(2, bins.heightStepM * view.scale)}
                  fill='none'
                  stroke='currentColor'
                  strokeWidth={1}
                />
              )}

              <AxisLeft
                scale={yScale}
                left={view.originX}
                tickValues={yTicks}
                stroke='currentColor'
                tickStroke='currentColor'
                tickFormat={(v) => heightTick(Number(v))}
                tickLabelProps={tickLabelProps({ textAnchor: 'end', dx: '-0.25em', dy: '0.25em', className: 'sv-num' })}
              />
              <text
                data-testid='clearance-section-y-axis-label'
                x={2}
                // Over the offset histogram when there is one, not beside it.
                y={showHist ? TICK_FONT : Math.max(TICK_FONT, view.originY - TICK_FONT / 2 - 4)}
                fontSize={TICK_FONT}
                fill='currentColor'
                opacity={0.7}
              >
                {t('clearance.section.heightAxis')}
              </text>
              <AxisBottom
                scale={xScale}
                top={plotBottom}
                tickValues={xTicks}
                stroke='currentColor'
                tickStroke='currentColor'
                tickFormat={(v) => signedOffsetTick(Number(v))}
                tickLabelProps={tickLabelProps({ textAnchor: 'middle', className: 'sv-num' })}
              />
              {/* "CL" under the 0 tick: the centreline keeps its name without
                the axis switching to an L / R convention nothing else in the
                clearance UI uses. Not translated — it is the drawing
                convention's own abbreviation, as on the station section. */}
              {centreOnAxis && (
                <text
                  data-testid='clearance-section-cl'
                  x={xScale(0)}
                  y={plotBottom + 27}
                  fontSize={TICK_FONT}
                  fill='currentColor'
                  opacity={0.7}
                  textAnchor='middle'
                >
                  CL
                </text>
              )}
              <text
                data-testid='clearance-section-x-axis-label'
                x={plotRight + 6 + histRoom}
                y={plotBottom + 4}
                fontSize={TICK_FONT}
                fill='currentColor'
                opacity={0.7}
                textAnchor='start'
              >
                {t('clearance.section.offsetAxis')}
              </text>

              {/* One side of the gauge in emphasis while its "Where it fouls"
                row is hovered: the edges that row's returns were counted
                against, in the row's own ink. */}
              {sideHover && (
                <g data-testid='clearance-section-side-highlight' data-side={sideHover} aria-hidden>
                  {sideEdges[sideHover].map(([p, q], i) => (
                    <line
                      key={i}
                      x1={toPxX(view, bins, p[0])}
                      y1={toPxY(view, bins, p[1])}
                      x2={toPxX(view, bins, q[0])}
                      y2={toPxY(view, bins, q[1])}
                      stroke={worstFoul?.side === sideHover ? sideInk(worstFoul.kind) : 'var(--sv-walk)'}
                      strokeWidth={4}
                      strokeLinecap='round'
                      opacity={0.9}
                    />
                  ))}
                </g>
              )}

              {/* Marginal histograms on the drawing's own axes: violating
                (red) under near-miss (amber) points per offset column above
                the grid, per height row beside it. Each column sits exactly
                over its column of cells — same scale, same origin. */}
              {showHist && marginals && (
                <>
                  <g data-testid='clearance-section-hist-cols' aria-hidden>
                    <line
                      x1={view.originX}
                      x2={plotRight}
                      y1={histBaseY + 0.5}
                      y2={histBaseY + 0.5}
                      stroke='currentColor'
                      opacity={0.25}
                    />
                    {Array.from(marginals.colViolations, (v, col) => {
                      const n = marginals.colNearMiss[col];
                      if (v + n === 0) return null;
                      const [hv, hn] = histLens(v, n, marginals.colPeak);
                      const x = view.originX + col * colPx;
                      const w = Math.max(1, colPx - (colPx > 3 ? 1 : 0));
                      return (
                        <g key={col}>
                          {v > 0 && <rect x={x} y={histBaseY - hv} width={w} height={hv} fill={CLEARANCE_FILL.violation} />}
                          {n > 0 && (
                            <rect x={x} y={histBaseY - hv - hn} width={w} height={hn} fill={CLEARANCE_FILL.nearMiss} />
                          )}
                        </g>
                      );
                    })}
                  </g>
                  <g data-testid='clearance-section-hist-rows' aria-hidden>
                    <line
                      x1={histBaseX - 0.5}
                      x2={histBaseX - 0.5}
                      y1={view.originY}
                      y2={plotBottom}
                      stroke='currentColor'
                      opacity={0.25}
                    />
                    {Array.from(marginals.rowViolations, (v, row) => {
                      const n = marginals.rowNearMiss[row];
                      if (v + n === 0) return null;
                      const [wv, wn] = histLens(v, n, marginals.rowPeak);
                      const h = Math.max(1, rowPx - (rowPx > 3 ? 1 : 0));
                      const y = plotBottom - (row + 1) * rowPx + (rowPx > 3 ? 0.5 : 0);
                      return (
                        <g key={row}>
                          {v > 0 && <rect x={histBaseX} y={y} width={wv} height={h} fill={CLEARANCE_FILL.violation} />}
                          {n > 0 && (
                            <rect x={histBaseX + wv} y={y} width={wn} height={h} fill={CLEARANCE_FILL.nearMiss} />
                          )}
                        </g>
                      );
                    })}
                  </g>
                  {/* The hovered column / row, outlined across the grid too,
                    so the bar and the cells it sums read as one thing. */}
                  {histHover && (
                    <rect
                      data-testid='clearance-section-hist-guide'
                      className='cl-section-hover text-slate-900 dark:text-stone-100'
                      x={histHover.axis === 'col' ? view.originX + histHover.i * colPx : view.originX}
                      y={histHover.axis === 'col' ? histBaseY - HIST_PX : plotBottom - (histHover.i + 1) * rowPx}
                      width={histHover.axis === 'col' ? Math.max(2, colPx) : histBaseX + HIST_PX - view.originX}
                      height={histHover.axis === 'col' ? plotBottom - (histBaseY - HIST_PX) : Math.max(2, rowPx)}
                      fill='currentColor'
                      fillOpacity={0.06}
                      stroke='currentColor'
                      strokeOpacity={0.45}
                      strokeWidth={1}
                    />
                  )}
                  <rect
                    data-testid='clearance-section-hist-cols-hit'
                    x={view.originX}
                    y={histBaseY - HIST_PX}
                    width={view.width}
                    height={HIST_PX}
                    fill='transparent'
                    style={{ pointerEvents: 'all' }}
                    onPointerMove={onHistMove('col')}
                    onPointerLeave={onHistLeave}
                  />
                  <rect
                    data-testid='clearance-section-hist-rows-hit'
                    x={histBaseX}
                    y={view.originY}
                    width={HIST_PX}
                    height={view.height}
                    fill='transparent'
                    style={{ pointerEvents: 'all' }}
                    onPointerMove={onHistMove('row')}
                    onPointerLeave={onHistLeave}
                  />
                </>
              )}

              {/* Hit overlay over the grid, drawn LAST so it catches the pointer
                before the marks; `transparent`, not `none`, keeps it
                hit-testable. */}
              <rect
                data-testid='clearance-section-overlay'
                x={view.originX}
                y={view.originY}
                width={view.width}
                height={view.height}
                fill='transparent'
                style={{ cursor: 'crosshair', pointerEvents: 'all' }}
                onPointerMove={onOverlayMove}
                onPointerLeave={onOverlayLeave}
              />
            </svg>

            {tooltipOpen && hover && tipRows.length > 0 && (
              <TooltipInPortal
                top={tooltipTop}
                left={tooltipLeft}
                style={FLOATING_TOOLTIP_STYLE}
                className='pointer-events-none !rounded-md !bg-slate-800 !px-2 !py-1.5 !text-xs !text-white !shadow-lg'
              >
                <div data-testid='clearance-section-tooltip'>
                  <TooltipCard
                    station={t('clearance.section.tipTitle', {
                      offset: formatSigned(offsetAt(bins, hover.col), 2),
                      height: formatFixed(heightAt(bins, hover.row), 2),
                    })}
                    rows={tipRows}
                  />
                </div>
              </TooltipInPortal>
            )}
            {tooltipOpen && !hover && histTip && (
              <TooltipInPortal
                top={tooltipTop}
                left={tooltipLeft}
                style={FLOATING_TOOLTIP_STYLE}
                className='pointer-events-none !rounded-md !bg-slate-800 !px-2 !py-1.5 !text-xs !text-white !shadow-lg'
              >
                <div data-testid='clearance-section-hist-tooltip'>
                  <TooltipCard station={histTip.title} rows={histTip.rows} />
                </div>
              </TooltipInPortal>
            )}
          </div>
        </div>

        {/* ---- The side area: the interval's figures, where it fouls, the
              corridor. Takes the row's remaining width (see the header for
              what gives way when it is small). ---- */}
        <div
          data-testid='clearance-section-side'
          data-layout={layout}
          className={cn(
            'min-h-0 min-w-0 flex-1 overflow-hidden border-l border-slate-200 pl-3 dark:border-stone-700',
            '@max-[700px]:order-first @max-[700px]:flex-none @max-[700px]:border-l-0 @max-[700px]:pl-0',
            layout === 'two' || layout === 'three' ? 'grid gap-x-4 gap-y-2' : 'flex flex-col gap-3',
          )}
          style={
            layout === 'two'
              ? {
                  gridTemplateColumns: sideW < 600 ? '12rem minmax(0, 1fr)' : 'minmax(12rem, 15rem) minmax(0, 1fr)',
                  gridTemplateRows: 'minmax(0, 1fr) auto',
                }
              : layout === 'three'
                ? { gridTemplateColumns: 'minmax(13rem, 15rem) minmax(17rem, 28rem) minmax(0, 1fr)' }
                : undefined
          }
        >
        {/* ---- The interval's figures (spec §B4): a definition list. Under
              700 px of pane it folds into ONE row above the drawing, keeping
              the figures a reader quotes and dropping the inner near-miss
              rungs and the cell note. ---- */}
        <section
          data-testid='clearance-section-readout'
          aria-label={t('clearance.section.stats')}
          className={cn(
            // Below 560 px the folded row WRAPS (each figure stays whole): one
            // clipped line lost the unit of Max intrusion and everything after.
            'flex min-h-0 min-w-0 flex-col gap-2 overflow-y-auto @max-[700px]:w-full @max-[700px]:flex-row @max-[700px]:items-center @max-[700px]:gap-3 @max-[700px]:overflow-hidden @max-[700px]:whitespace-nowrap @max-[560px]:flex-wrap @max-[560px]:gap-y-1 @max-[560px]:overflow-visible',
            // Stacked under "Where it fouls" only by its content height.
            layout === 'stack' && 'shrink-0',
          )}
        >
          <div className='flex shrink-0 items-center justify-between gap-2'>
            <span data-testid='clearance-section-binof' className={cn(DOCK_TEXT.metaMuted, 'sv-num uppercase')}>
              {t('clearance.section.binOf', {
                n: (clampedIndex + 1).toLocaleString(),
                total: nBins.toLocaleString(),
              })}
            </span>
            <VerdictBadge verdict={verdict} className='text-[11px]' />
          </div>
          <dl className='grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-3 gap-y-1 @max-[700px]:flex @max-[700px]:min-w-0 @max-[700px]:gap-3 @max-[560px]:flex-wrap @max-[560px]:gap-y-1'>
            <div className='contents @max-[700px]:flex @max-[700px]:items-baseline @max-[700px]:gap-1'>
              <dt className={DOCK_TEXT.metaMuted} title={t('clearance.table.violationsHelp')}>
                {t('clearance.kpi.violations')}
              </dt>
              <dd
                data-testid='clearance-section-stat-violations'
                className='sv-num text-right font-semibold'
                style={bin.violationCount > 0 ? { color: CLEARANCE_FILL.violation } : undefined}
              >
                {bin.violationCount.toLocaleString()}
              </dd>
            </div>
            <div className='contents @max-[700px]:flex @max-[700px]:items-baseline @max-[700px]:gap-1'>
              <dt className={DOCK_TEXT.metaMuted} title={t('clearance.table.maxIntrusionHelp')}>
                {t('clearance.kpi.worst')}
              </dt>
              <dd className='sv-num text-right font-semibold'>
                {bin.maxIntrusionM !== null ? (
                  <span style={{ color: CLEARANCE_FILL.violation }}>{formatFixed(bin.maxIntrusionM, 3)} m</span>
                ) : (
                  <span className='font-normal text-slate-500 dark:text-stone-400'>
                    {t('clearance.section.statNone')}
                  </span>
                )}
              </dd>
            </div>
            {/* Where, as well as how deep: the marker on the drawing is one ring
              among many cells, the figures are what get quoted. */}
            {bin.maxIntrusionM !== null && bin.worstOffsetM !== null && bin.worstHeightM !== null && (
              <dd
                data-testid='clearance-section-worst-readout'
                className={cn(DOCK_TEXT.metaMuted, 'sv-num col-span-2 -mt-0.5 text-right @max-[700px]:mt-0')}
              >
                {t('clearance.section.statWorstAt', {
                  offset: formatSigned(bin.worstOffsetM, 2),
                  height: formatFixed(bin.worstHeightM, 2),
                })}
              </dd>
            )}
            <dt
              className={cn(DOCK_TEXT.metaMuted, 'col-span-2 mt-1 @max-[700px]:hidden', short && 'hidden')}
              title={t('clearance.table.nearMissHelp')}
            >
              {t('clearance.table.colNearMiss')}
            </dt>
            {nearRows.map((r) => (
              <div
                key={r.key}
                data-testid={`clearance-section-stat-near-${r.key}`}
                className={cn(
                  'contents',
                  r.outer ? '@max-[700px]:flex @max-[700px]:items-baseline @max-[700px]:gap-1' : '@max-[700px]:hidden',
                  !r.outer && short && 'hidden',
                )}
              >
                <dt className={cn(DOCK_TEXT.metaMuted, 'sv-num @max-[700px]:pl-0', !short && 'pl-2')}>
                  {/* Wide: an indented rung under "Near misses". Compact (a
                    narrow pane, or a dock too short for the ladder): the outer
                    rung alone, so it names itself. */}
                  <span className={cn(r.outer && '@max-[700px]:hidden', r.outer && short && 'hidden')}>
                    {t('clearance.table.nearMissBand', { value: formatFixed(r.band, 2) })}
                  </span>
                  {r.outer && (
                    <span className={cn('@max-[700px]:inline', short ? 'inline' : 'hidden')}>
                      {t('clearance.section.legendNearMiss', { band: formatFixed(r.band, 2) })}
                    </span>
                  )}
                </dt>
                {/* Plain ink: the caution amber is a FILL colour and fails as
                  text contrast on the light surface. */}
                <dd className='sv-num text-right'>{r.n.toLocaleString()}</dd>
              </div>
            ))}
            {/* Dropped on a phone: the least-quoted figure, and its line is the
                drawing's height (the Table tab has it per interval). */}
            <div className='contents @max-[700px]:flex @max-[700px]:items-baseline @max-[700px]:gap-1 @max-[560px]:hidden'>
              <dt className={cn(DOCK_TEXT.metaMuted, 'mt-1 @max-[700px]:mt-0')} title={t('clearance.table.pointsHelp')}>
                {t('clearance.section.statPoints')}
              </dt>
              <dd data-testid='clearance-section-stat-points' className='sv-num mt-1 text-right @max-[700px]:mt-0'>
                {bin.pointCount.toLocaleString()}
              </dd>
            </div>
          </dl>
          <p className={cn(DOCK_TEXT.metaMuted, 'sv-num mt-auto @max-[700px]:hidden', short && 'hidden')}>
            {t('clearance.section.cellNote', {
              w: formatFixed(bins.offsetStepM, 2),
              h: formatFixed(bins.heightStepM, 2),
            })}
          </p>
        </section>

        {showFouls && fouls && (
          <section
            data-testid='clearance-section-fouls'
            aria-labelledby={`${uid}-fouls`}
            className={cn(
              'flex min-h-0 min-w-0 flex-col gap-1',
              (layout === 'two' || layout === 'three') && 'border-l border-slate-200 pl-4 dark:border-stone-700',
            )}
          >
            <div className='flex h-4 shrink-0 items-center gap-1.5 whitespace-nowrap'>
              <h3 id={`${uid}-fouls`} className={cn(DOCK_TEXT.metaMuted, 'font-normal uppercase')}>
                {t('clearance.section.fouls')}
              </h3>
              <span
                role='img'
                aria-label={t('clearance.section.foulsHelp')}
                title={t('clearance.section.foulsHelp')}
                className='inline-flex cursor-help items-center text-slate-400 dark:text-stone-500'
              >
                <Info className='size-3' aria-hidden />
              </span>
            </div>
            <table className='w-full border-collapse text-[11px] leading-4'>
              <thead>
                <tr className='whitespace-nowrap text-slate-500 dark:text-stone-400'>
                  <th scope='col' className='py-0.5 pr-2 pl-1.5 text-left font-normal'>
                    {t('clearance.section.colSide')}
                  </th>
                  {!foulsMinimal && (
                    <th scope='col' className='px-1.5 py-0.5 text-right font-normal'>
                      {t('clearance.section.colViolating')}
                    </th>
                  )}
                  <th scope='col' className='px-1.5 py-0.5 text-right font-normal' title={t('clearance.section.foulsHelp')}>
                    {t('clearance.section.colIntrusion')}
                  </th>
                  {!foulsCompact && (
                    <th
                      scope='col'
                      className='px-1.5 py-0.5 text-right font-normal'
                      title={t('clearance.section.tipNearMiss', { band: formatFixed(outerBandM, 2) })}
                    >
                      {t('clearance.section.colNearMiss')}
                    </th>
                  )}
                  <th
                    scope='col'
                    className='py-0.5 pr-1.5 pl-1.5 text-right font-normal'
                    title={t('clearance.section.colTightestHelp')}
                  >
                    {t('clearance.section.colTightest')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {fouls.map((f) => {
                  const hi = worstFoul?.side === f.side ? worstFoul.kind : null;
                  const ink = hi ? sideInk(hi) : null;
                  return (
                    <tr
                      key={f.side}
                      data-testid={`clearance-section-foul-${f.side}`}
                      data-worst={hi ?? undefined}
                      onPointerEnter={() => setSideHover(f.side)}
                      onPointerLeave={() => setSideHover(null)}
                      className='border-t border-slate-100 dark:border-stone-800'
                      style={
                        ink
                          ? {
                              background: `color-mix(in srgb, ${ink} 11%, transparent)`,
                              boxShadow: `inset 2px 0 0 ${ink}`,
                            }
                          : undefined
                      }
                    >
                      <th scope='row' className='py-1 pr-2 pl-1.5 text-left font-normal whitespace-nowrap'>
                        <span className='inline-flex items-center gap-1.5'>
                          <svg width={12} height={12} aria-hidden className='shrink-0'>
                            <rect
                              x={1.5}
                              y={1.5}
                              width={9}
                              height={9}
                              fill='none'
                              stroke='currentColor'
                              strokeOpacity={0.3}
                            />
                            <line
                              x1={SIDE_GLYPH[f.side][0]}
                              y1={SIDE_GLYPH[f.side][1]}
                              x2={SIDE_GLYPH[f.side][2]}
                              y2={SIDE_GLYPH[f.side][3]}
                              stroke={ink ?? 'currentColor'}
                              strokeWidth={2}
                              strokeLinecap='round'
                            />
                          </svg>
                          <span className={hi ? 'font-semibold' : undefined}>{t(SIDE_LABEL_KEY[f.side])}</span>
                        </span>
                      </th>
                      {!foulsMinimal && (
                        <td
                          className={cn('sv-num px-1.5 text-right', f.violations > 0 ? 'font-semibold' : 'opacity-60')}
                          style={f.violations > 0 ? { color: CLEARANCE_FILL.violation } : undefined}
                        >
                          {f.violations.toLocaleString()}
                        </td>
                      )}
                      <td
                        className={cn('sv-num px-1.5 text-right', f.maxIntrusionM === null && 'opacity-60')}
                        style={f.maxIntrusionM !== null ? { color: CLEARANCE_FILL.violation } : undefined}
                      >
                        {f.maxIntrusionM !== null ? formatFixed(f.maxIntrusionM, 3) : '—'}
                      </td>
                      {!foulsCompact && (
                        <td className={cn('sv-num px-1.5 text-right', f.nearMissPoints === 0 && 'opacity-60')}>
                          {f.nearMissPoints.toLocaleString()}
                        </td>
                      )}
                      <td
                        className={cn(
                          'sv-num pr-1.5 pl-1.5 text-right',
                          f.tightestMarginM === null && 'opacity-60',
                          hi === 'nearMiss' && 'font-semibold',
                        )}
                      >
                        {f.tightestMarginM !== null ? formatFixed(f.tightestMarginM, 3) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {layout === 'two' && rankedBlock}
          </section>
        )}

        {showStrip && nBins > 0 && (
          <section
            data-testid='clearance-section-corridor'
            aria-labelledby={`${uid}-corridor`}
            className={cn(
              'flex min-h-0 min-w-0 flex-col gap-1',
              layout === 'two' && 'col-span-2 border-t border-slate-200 pt-2 dark:border-stone-700',
              layout === 'three' && 'border-l border-slate-200 pl-4 dark:border-stone-700',
              layout === 'stack' && 'shrink-0',
            )}
          >
            <div className='flex h-4 shrink-0 items-center gap-2 whitespace-nowrap'>
              <h3 id={`${uid}-corridor`} className={cn(DOCK_TEXT.metaMuted, 'shrink-0 font-normal uppercase')}>
                {t('clearance.section.corridor')}
              </h3>
              <span
                data-testid='clearance-section-corridor-readout'
                className={cn(DOCK_TEXT.meta, 'sv-num min-w-0 truncate')}
              >
                {stripReadout}
              </span>
            </div>
            {/* A slider over the run's intervals: press or drag to stand on
              one, the pane's own keys to step. Bars on a log scale (the
              section's density rule), the walked interval in the walk ink. */}
            <div
              role='slider'
              tabIndex={0}
              data-testid='clearance-section-corridor-strip'
              aria-label={t('clearance.section.corridorLabel')}
              aria-valuemin={1}
              aria-valuemax={nBins}
              aria-valuenow={clampedIndex + 1}
              aria-valuetext={`${rangeText}: ${violationsText}`}
              title={t('clearance.section.corridorHelp')}
              onKeyDown={(e) => {
                stepByKey(e);
              }}
              onPointerDown={onStripDown}
              onPointerMove={onStripMove}
              onPointerUp={onStripUp}
              onPointerCancel={onStripUp}
              onPointerLeave={() => setStripHover(null)}
              className={cn(
                'cl-section relative w-full cursor-pointer touch-none select-none',
                layout === 'three' ? (ranked.length > 0 ? 'h-20 shrink-0' : 'max-h-36 min-h-10 flex-1') : 'h-10 shrink-0',
              )}
            >
              <svg
                className='absolute inset-0 h-full w-full overflow-visible'
                viewBox={`0 0 ${strip.length} 100`}
                preserveAspectRatio='none'
                aria-hidden
              >
                <rect x={stripCurrent} y={0} width={1} height={100} fill='var(--sv-walk)' fillOpacity={0.16} />
                {strip.map((b, k) => {
                  const fill = TONE_FILL[b.tone];
                  if (!fill) return null;
                  const h =
                    b.tone === 'violation'
                      ? 14 + 86 * corridorHeight(b.violations, stripPeak)
                      : b.tone === 'nearMiss'
                        ? 14
                        : 5;
                  return (
                    <rect
                      key={k}
                      x={k + barInset}
                      y={100 - h}
                      width={1 - 2 * barInset}
                      height={h}
                      fill={fill}
                      fillOpacity={b.tone === 'clear' ? 0.45 : 1}
                    />
                  );
                })}
                {stripHoverBucket !== null && stripHoverBucket !== stripCurrent && (
                  <rect
                    x={stripHoverBucket}
                    y={0}
                    width={1}
                    height={100}
                    fill='currentColor'
                    fillOpacity={0.08}
                  />
                )}
                <line
                  x1={0}
                  x2={strip.length}
                  y1={100}
                  y2={100}
                  stroke='currentColor'
                  strokeOpacity={0.3}
                  vectorEffect='non-scaling-stroke'
                />
                <rect
                  data-testid='clearance-section-corridor-current'
                  x={stripCurrent}
                  y={0}
                  width={1}
                  height={100}
                  fill='none'
                  stroke='var(--sv-walk)'
                  strokeWidth={1.5}
                  vectorEffect='non-scaling-stroke'
                />
              </svg>
            </div>
            <div className={cn(DOCK_TEXT.metaMuted, 'sv-num flex shrink-0 justify-between gap-2 whitespace-nowrap')}>
              <span>{staOnly(firstBin.staStartDisplayM, 0)}</span>
              <span>{staOnly(lastBin.staEndDisplayM, 0)}</span>
            </div>
            {layout === 'three' && rankedBlock}
          </section>
        )}
        </div>
      </div>

      {/* The station section's visually-hidden table — the drawing as rows,
          a SIBLING of the `role="application"` pane so assistive tech still
          exposes it as a table. */}
      <HiddenDataTable
        caption={
          srTable.stride > 1
            ? t('clearance.section.srCaptionGrouped', { station: rangeText, n: srTable.stride })
            : t('clearance.section.srCaption', { station: rangeText })
        }
        headers={[
          t('clearance.section.colOffset'),
          t('clearance.section.tipPoints'),
          t('clearance.section.tipViolating'),
          t('clearance.section.colLow'),
          t('clearance.section.colHigh'),
        ]}
        rows={srTable.rows.map((r, i) => ({
          key: i,
          cells: [
            `${formatSigned(r.fromOffsetM, 2)} – ${formatSigned(r.toOffsetM, 2)}`,
            r.points.toLocaleString(),
            r.violations.toLocaleString(),
            r.lowM === null ? '—' : formatFixed(r.lowM, 2),
            r.highM === null ? '—' : formatFixed(r.highM, 2),
          ],
        }))}
        currentRow={null}
      />
    </div>
  );
};
